import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Alert
} from '@mui/material';
import { useParams } from 'react-router-dom';
import CampaignService from '../../services/CampaignService';
import { Campaign } from '../../types/Campaign';
import { NavigationSection } from './NavigationSection';
import Sidebar from './Sidebar';

// Import phase-specific components
import Applications from './Phases/Prep/Applications';
import LaunchPanel from './Phases/Prep/LaunchPanel';
import CommunicationTemplatesPanel from './Phases/Prep/CommunicationTemplatesPanel';
import Overview from './Phases/Prep/Overview';
import Dates from './Phases/Prep/Dates';

const CampaignOverview: React.FC = () => {
    const { campaignId } = useParams<{ campaignId: string }>();
    const { organizationId } = useParams<{ organizationId: string }>();
    const [activeSection, setActiveSection] = useState<NavigationSection>(NavigationSection.PURPOSE);
    const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [error, setError] = useState<string | null>(null);
    const campaignService = useMemo(() => new CampaignService(), []);

    // Refs to hold the pending changes from child components
    const pendingChangesRef = useRef<Partial<Campaign> | null>(null);

    // Method to capture pending changes from child components
    const handlePendingChanges = (changes: Partial<Campaign>) => {
        pendingChangesRef.current = changes;
    };

    useEffect(() => {
        const fetchCampaignData = async () => {
            if (!campaignId || !organizationId) {
                setError('Campaign ID or Organization ID is missing');
                return;
            }

            try {
                const result = await campaignService.getCampaignById(organizationId, campaignId);
                setCampaign(result);
            } catch (error) {
                console.error('Error fetching campaign details:', error);
                setError('Failed to load campaign details');
            }
        };

        fetchCampaignData();
    }, [campaignId, organizationId, campaignService]);

    const handleSaveCampaign = async (updatedCampaign: Partial<Campaign>) => {
        if (!campaignId || !organizationId) return campaign as Campaign;

        try {
            if (!campaign) {
                console.error('Campaign is null, cannot update');
                throw new Error('Campaign not found');
            }
            const campaignUpdate: Partial<Campaign> = {
                ...updatedCampaign,
                id: campaignId,
                organization_id: organizationId
            };

            const result = await campaignService.updateCampaign(
                organizationId,
                campaignId,
                campaignUpdate
            );

            if (!result) {
                throw new Error('Failed to update campaign');
            }

            setCampaign(result);
            return result;
        } catch (error) {
            console.error('Error saving campaign details:', error);
            throw error;
        }
    };

    // Determine if a navigation section is allowed based on the current campaign phase
    const isSectionAllowed = (section: NavigationSection): boolean => {
        if (!campaign) return false;

        const { status } = campaign;

        // Map navigation sections to their respective campaign phases
        const prepSections = [
            NavigationSection.PURPOSE,
            NavigationSection.DATES,
            NavigationSection.APPLICATIONS,
            NavigationSection.COMMUNICATION,
            NavigationSection.LAUNCH
        ];

        const matchingSections = [
            NavigationSection.MATCHING_OVERVIEW,
            NavigationSection.MENTEE_APPLICANTS,
            NavigationSection.MENTOR_APPLICANTS,
            NavigationSection.SCORES,
            NavigationSection.MATCHES
        ];

        const runningSections = [
            NavigationSection.RUNNING_OVERVIEW,
            NavigationSection.MATCHES_STATUS,
            NavigationSection.REMATCHING
        ];

        const followUpSections = [
            NavigationSection.FOLLOWUP_OVERVIEW,
            NavigationSection.FINAL_STATUS
        ];

        // Only allow sections from the current phase or earlier phases
        switch (status) {
            case 'prep':
                return prepSections.includes(section);
            case 'matching':
                return [...prepSections, ...matchingSections].includes(section);
            case 'running':
                return [...prepSections, ...matchingSections, ...runningSections].includes(section);
            case 'followUp':
                return true; // All sections are available in the final phase
            default:
                return false;
        }
    };

    const handleSectionChange = async (section: NavigationSection) => {
        // Don't allow changing to sections that aren't enabled for the current phase
        if (!isSectionAllowed(section)) {
            return;
        }

        try {
            // If there are pending changes, save them before changing sections
            if (pendingChangesRef.current && campaign) {
                // Save the pending changes
                await handleSaveCampaign(pendingChangesRef.current);
                // Clear the pending changes
                pendingChangesRef.current = null;
            }

            // Now change the section
            setActiveSection(section);
        } catch (error) {
            console.error('Error saving before section change:', error);
            // Still change the section even if save fails
            setActiveSection(section);
        }
    };

    const toggleSidebar = () => {
        setSidebarCollapsed(!sidebarCollapsed);
    };

    const renderMainContent = () => {
        if (error) {
            return (
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            );
        }

        if (!campaign) {
            return (
                <Box sx={{ p: 3 }}>
                    <Alert severity="warning">Campaign not found</Alert>
                </Box>
            );
        }

        switch (activeSection) {
            case NavigationSection.PURPOSE:
                return <Overview campaign={campaign} onSave={handleSaveCampaign} onPendingChanges={handlePendingChanges} />;

            case NavigationSection.DATES:
                return <Dates campaign={campaign} onSave={handleSaveCampaign} onPendingChanges={handlePendingChanges} />;

            case NavigationSection.APPLICATIONS:
                return campaign && organizationId ? (
                    <Applications campaignId={campaign.id} organizationId={organizationId} />
                ) : null;

            case NavigationSection.COMMUNICATION:
                return campaign && organizationId ? (
                    <CommunicationTemplatesPanel campaignId={campaign.id} organizationId={organizationId} />
                ) : null;

            case NavigationSection.LAUNCH:
                return campaign && organizationId ? (
                    <LaunchPanel
                        campaignId={campaign.id}
                        organizationId={organizationId}
                        isLaunched={false}
                    />
                ) : null;

            default:
                // For any sections that don't have components yet
                return (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="info">
                            The {activeSection} section is under development.
                        </Alert>
                    </Box>
                );
        }
    };

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
            {/* Sidebar */}
            <Sidebar
                activeSection={activeSection}
                handleSectionChange={handleSectionChange}
                isSectionAllowed={isSectionAllowed}
                sidebarCollapsed={sidebarCollapsed}
                toggleSidebar={toggleSidebar}
                campaignName={campaign?.details?.name || 'Campaign'}
            />

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    height: '100%',
                    overflow: 'auto',
                    p: 3,
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                    boxShadow: 'inset 0px 0px 5px rgba(0,0,0,0.05)'
                }}
            >
                {renderMainContent()}
            </Box>
        </Box>
    );
};

export default CampaignOverview;